import { useState, useEffect } from "react";
import { Space, Tooltip, message, Popconfirm, Input, Button } from "antd";
import { useQuery, useMutation, useQueryClient } from "react-query";
import skillManagementService from "./skillManagementService";
import useDebounce from "../../../../core/hooks/useDebounce";

const useSkillsManagement = () => {
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedValue = useDebounce(searchQuery, 1000);
  const [options, setOptions] = useState({});
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [newSkill, setNewSkill] = useState(null);
  const [filteredSkills, setFilteredSkills] = useState([]);

  const {
    data: skills,
    isLoading,
    error,
  } = useQuery(
    ["skills", options],
    () => skillManagementService.getSkillsList(options),
    { keepPreviousData: true }
  );

  useEffect(() => {
    if (!skills) return;

    if (!searchQuery.trim()) {
      setFilteredSkills(skills.filter((skill) => !skill.id.startsWith("new-")));
    } else {
      const searchTerm = searchQuery.toLowerCase();
      setFilteredSkills(
        skills
          .filter(
            (skill) =>
              skill.name.toLowerCase().includes(searchTerm) ||
              (Array.isArray(skill.categories) &&
                skill.categories.some(
                  (cat) =>
                    (cat.categoryName &&
                      cat.categoryName.toLowerCase().includes(searchTerm)) ||
                    (cat.subCategory &&
                      cat.subCategory.toLowerCase().includes(searchTerm)) ||
                    (cat.subSubCategory &&
                      cat.subSubCategory.toLowerCase().includes(searchTerm))
                ))
          )
          .filter((skill) => !skill.id.startsWith("new-"))
      );
    }
  }, [searchQuery, skills]);

  const createSkillMutation = useMutation(skillManagementService.createSkill, {
    onSuccess: () => {
      queryClient.invalidateQueries("skills");
      message.success("Skill added successfully");
      setNewSkill(null);
      setEditingKey(null);
    },
  });

  const updateSkillMutation = useMutation(
    ({ id, data }) => skillManagementService.updateSkill(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("skills");
        message.success("Skill updated successfully");
        setEditingKey(null);
      },
    }
  );

  const deleteSkillMutation = useMutation(skillManagementService.deleteSkill, {
    onSuccess: () => {
      queryClient.invalidateQueries("skills");
      message.success("Skill deleted successfully");
    },
    onError: (error) => {
      const backendMessage =
        error?.response?.data?.message || "Failed to delete skill.";
      message.error(backendMessage);
    },
  });

  const toggleSkillStatusMutation = useMutation(
    ({ id, isActivated }) =>
      isActivated
        ? skillManagementService.deactivateSkill(id)
        : skillManagementService.activateSkill(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("skills");
        message.success("Skill status updated successfully");
      },
      onError: (error) => {
        const backendMessage =
          error?.response?.data?.message || "Failed to update status.";
        message.error(backendMessage);
      },
    }
  );

  const handleAddSkill = () => {
    if (editingKey && editingKey.startsWith("new-")) {
      message.warning("Complete the current new skill before adding another.");
      return;
    }

    const tempId = `new-${Math.random()}`;
    const emptySkill = {
      id: tempId,
      name: "",
      categories: [{ categoryName: "", subCategory: "", subSubCategory: "" }],
      isActivated: true,
    };

    setNewSkill(emptySkill);
    setEditingKey(tempId);

    setFilteredSkills((prev) => [emptySkill, ...(skills || [])]);
  };

  const handleEditSkill = (record) => {
    setEditingKey(record.id);
    setSelectedSkill({ ...record });
  };

  const handleSave = async (id) => {
    const skillToSave = id.startsWith("new-") ? newSkill : selectedSkill;

    if (!skillToSave || !skillToSave.name.trim()) {
      message.error("Skill name is required.");
      return;
    }

    const formattedData = {
      name: skillToSave.name,
      categories: skillToSave.categories?.map((cat) => ({
        categoryName: cat.categoryName || "",
        subCategory: cat.subCategory || "",
        subSubCategory: cat.subSubCategory || "",
      })),
    };

    console.log("🟡 Data to send:", JSON.stringify(formattedData, null, 2));

    try {
      if (id.startsWith("new-")) {
        await createSkillMutation.mutateAsync(formattedData);
        setNewSkill(null);
      } else {
        await updateSkillMutation.mutateAsync({ id, data: formattedData });
      }
      setEditingKey(null);
    } catch (error) {
      console.error("❌ Update error:", error.response?.data || error);
      message.error("Failed to save skill. Please check the data format.");
    }
  };

  const handleCancelEdit = () => {
    setEditingKey(null);
    setNewSkill(null);
    setSelectedSkill(null);

    setFilteredSkills((prev) =>
      prev.filter((skill) => !skill.id.startsWith("new-"))
    );
  };
  const handleChangeStatusSkill = (id, isActivated) => {
    toggleSkillStatusMutation.mutate({ id, isActivated });
  };

  const handleDeleteSkill = async (id) => {
    try {
      await deleteSkillMutation.mutateAsync(id);
    } catch (error) {
      const backendMessage =
        error?.response?.data?.message || "Failed to delete skill.";
      console.error("❌ Delete error:", error);
    }
  };

  const columns = [
    {
      title: "Skill Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) =>
        editingKey === record.id ? (
          <Input
            value={
              editingKey.startsWith("new-")
                ? newSkill?.name || ""
                : selectedSkill?.name || ""
            }
            onChange={(e) => {
              const newValue = e.target.value;
              if (editingKey.startsWith("new-")) {
                setNewSkill((prev) => ({
                  ...prev,
                  name: newValue,
                }));
              } else {
                setSelectedSkill((prev) => ({
                  ...prev,
                  name: newValue,
                }));
              }
            }}
          />
        ) : (
          text
        ),
    },
    {
      title: "Categories",
      dataIndex: "categories",
      key: "categories",
      render: (categories, record) =>
        editingKey === record.id ? (
          <>
            {(editingKey.startsWith("new-")
              ? newSkill?.categories
              : selectedSkill?.categories
            )?.map((cat, index) => (
              <div
                key={index}
                style={{ marginBottom: "8px", display: "flex", gap: "5px" }}
              >
                <Input
                  style={{ flex: 1 }}
                  placeholder="Category Name"
                  value={cat.categoryName || ""}
                  onChange={(e) => {
                    const newCategories = editingKey.startsWith("new-")
                      ? [...(newSkill?.categories || [])]
                      : [...(selectedSkill?.categories || [])];

                    newCategories[index].categoryName = e.target.value;

                    if (editingKey.startsWith("new-")) {
                      setNewSkill((prev) => ({
                        ...prev,
                        categories: newCategories,
                      }));
                    } else {
                      setSelectedSkill((prev) => ({
                        ...prev,
                        categories: newCategories,
                      }));
                    }
                  }}
                />

                <Input
                  style={{ flex: 1 }}
                  placeholder="Sub Category (optional)"
                  value={cat.subCategory || ""}
                  onChange={(e) => {
                    const newCategories = editingKey.startsWith("new-")
                      ? [...(newSkill?.categories || [])]
                      : [...(selectedSkill?.categories || [])];

                    newCategories[index].subCategory = e.target.value;

                    if (editingKey.startsWith("new-")) {
                      setNewSkill((prev) => ({
                        ...prev,
                        categories: newCategories,
                      }));
                    } else {
                      setSelectedSkill((prev) => ({
                        ...prev,
                        categories: newCategories,
                      }));
                    }
                  }}
                />

                <Input
                  style={{ flex: 1 }}
                  placeholder="Sub Sub Category (optional)"
                  value={cat.subSubCategory || ""}
                  onChange={(e) => {
                    const newCategories = editingKey.startsWith("new-")
                      ? [...(newSkill?.categories || [])]
                      : [...(selectedSkill?.categories || [])];

                    newCategories[index].subSubCategory = e.target.value;

                    if (editingKey.startsWith("new-")) {
                      setNewSkill((prev) => ({
                        ...prev,
                        categories: newCategories,
                      }));
                    } else {
                      setSelectedSkill((prev) => ({
                        ...prev,
                        categories: newCategories,
                      }));
                    }
                  }}
                />
              </div>
            ))}

            <Button
              type="dashed"
              onClick={() => {
                if (editingKey.startsWith("new-")) {
                  setNewSkill((prev) => ({
                    ...prev,
                    categories: [
                      ...(prev?.categories || []),
                      { categoryName: "", subCategory: "", subSubCategory: "" },
                    ],
                  }));
                } else {
                  setSelectedSkill((prev) => ({
                    ...prev,
                    categories: [
                      ...(prev?.categories || []),
                      { categoryName: "", subCategory: "", subSubCategory: "" },
                    ],
                  }));
                }
              }}
              style={{
                background: "transparent",
                border: "1px solid #d9d9d9",
                boxShadow: "none",
                pointerEvents: "auto",
                marginTop: "5px",
              }}
            >
              + Add Category
            </Button>
          </>
        ) : categories && Array.isArray(categories) ? (
          categories
            .map((cat) => {
              const filteredCategories = [
                cat.categoryName,
                cat.subCategory,
                cat.subSubCategory,
              ].filter((value) => value && value.trim() !== "");

              return filteredCategories.length > 0
                ? filteredCategories.join(" > ")
                : "N/A";
            })
            .join(" | ")
        ) : (
          "N/A"
        ),
    },
    {
      title: "Status",
      dataIndex: "isActivated",
      key: "isActivated",
      render: (isActivated) => (
        <span style={{ color: isActivated ? "green" : "red" }}>
          {isActivated ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <Space size="middle">
          {editingKey === record.id ? (
            <>
              <Button type="primary" onClick={() => handleSave(record.id)}>
                Save
              </Button>
              <Button onClick={() => setEditingKey(null)}>Cancel</Button>
            </>
          ) : (
            <Tooltip title="Edit Skill">
              <Button type="primary" onClick={() => handleEditSkill(record)}>
                Edit
              </Button>
            </Tooltip>
          )}
          <Popconfirm
            title="Are you sure you want to delete this skill?"
            onConfirm={() => handleDeleteSkill(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete Skill">
              <Button type="default" danger>
                Delete
              </Button>
            </Tooltip>
          </Popconfirm>
          <Popconfirm
            title={
              record.isActivated
                ? "Deactivate this skill?"
                : "Activate this skill?"
            }
            onConfirm={() =>
              handleChangeStatusSkill(record.id, record.isActivated)
            }
            okText="Yes"
            cancelText="No"
          >
            <Button type="default" danger={!record.isActivated}>
              {record.isActivated ? "Deactivate" : "Activate"}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return {
    skills: editingKey?.startsWith("new-")
      ? filteredSkills
      : filteredSkills.filter((skill) => !skill.id.startsWith("new-")) || [],

    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    options,
    setOptions,
    columns,
    handleAddSkill,
    handleEditSkill,
    handleSave,
    handleCancelEdit,
    handleChangeStatusSkill,
    handleDeleteSkill,
    selectedSkill,
    setSelectedSkill,
  };
};

export default useSkillsManagement;
